import { useState } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '../lib/auth';
import { Logo } from '../components/Logo';
import { Loader2, Check } from '../components/icons';

// Iter47n — Login/Signup page with:
//   • Se connecter tab (Nom + Prénom + Code d'accès)
//   • Créer un compte tab (Nom + Prénom + Email + Mot de passe)
// Rayan/Hugo/Axel keep their passcode-only fast path: if the "code d'accès"
// alone matches a hardcoded USER (RAYAN2026 etc.), we skip name check.
export default function LoginPage() {
  const { user, loginByCode, loginClassic, signup } = useAuth();
  const nav = useNavigate();
  const [mode, setMode] = useState('login'); // 'login' | 'signup' | 'pending'
  const [busy, setBusy] = useState(false);
  // Login form
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [code, setCode] = useState('');
  // Signup form
  const [suFirst, setSuFirst] = useState('');
  const [suLast, setSuLast] = useState('');
  const [suEmail, setSuEmail] = useState('');
  const [suPwd, setSuPwd] = useState('');

  if (user) return <Navigate to="/" replace />;

  const submitLogin = async (e) => {
    e?.preventDefault?.();
    if (!code.trim()) return;
    setBusy(true);
    try {
      // Founder/hardcoded fast-path: if the "code d'accès" alone matches, log in immediately
      // (this keeps Rayan/Hugo/Axel unchanged — they just type their code)
      try {
        const u = await loginByCode(code.trim().toUpperCase());
        toast.success(`Bienvenue, ${u.name}`);
        nav('/', { replace: true });
        return;
      } catch (_) {
        // fall through to name-based classic login
      }
      // Iter 62cj — if user typed passcode ONLY (no name filled), the passcode is
      // simply wrong → say so instead of the misleading "Nom et prénom requis".
      // Only prompt for name/surname if the user explicitly filled at least one.
      if (!firstName.trim() && !lastName.trim()) {
        toast.error('Code incorrect');
        return;
      }
      if (!firstName.trim() || !lastName.trim()) {
        toast.error('Nom et prénom requis pour ce code');
        return;
      }
      const u = await loginClassic(firstName.trim(), lastName.trim(), code);
      toast.success(`Bienvenue, ${u.name}`);
      nav('/', { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Identifiants incorrects');
    } finally { setBusy(false); }
  };

  const submitSignup = async (e) => {
    e?.preventDefault?.();
    if (!suFirst.trim() || !suLast.trim() || !suEmail.trim() || !suPwd.trim()) {
      toast.error('Tous les champs sont requis');
      return;
    }
    if (suPwd.length < 6) {
      toast.error('Mot de passe : 6 caractères minimum');
      return;
    }
    setBusy(true);
    try {
      await signup(suFirst.trim(), suLast.trim(), suEmail.trim().toLowerCase(), suPwd);
      setMode('pending');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur lors de la création');
    } finally { setBusy(false); }
  };

  const inputStyle = {
    width: '100%', fontSize: 15, fontFamily: 'Sora',
    fontWeight: 600, letterSpacing: 0.2, color: 'var(--t1)',
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'radial-gradient(circle at 30% 0%, rgba(46,163,242,0.10) 0%, transparent 50%), var(--bg-primary)',
      display: 'grid', placeItems: 'center', padding: '24px',
    }}>
      <div style={{ width: '100%', maxWidth: 420 }} className="fade-up">
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: 30 }}>
          <Logo size={68} />
          <h1 className="t-display-xl" style={{ marginTop: 14, marginBottom: 4 }}>ASA</h1>
          <p className="t-body-sm" style={{ color: 'var(--text-muted)', letterSpacing: 1.5, textTransform: 'uppercase' }}>
            Elevate Influence — Maison mère
          </p>
        </div>

        {mode === 'pending' ? (
          <div
            data-testid="signup-pending-screen"
            className="asa-card"
            style={{ padding: 28, textAlign: 'center' }}
          >
            <div style={{
              width: 60, height: 60, borderRadius: '50%',
              background: 'rgba(52,211,153,0.15)', border: '1.5px solid rgba(52,211,153,0.40)',
              display: 'grid', placeItems: 'center', margin: '0 auto 16px',
            }}>
              <Check size={26} color="#34D399" strokeWidth={2.4} />
            </div>
            <h2 className="asa-heading" style={{ fontSize: '1.15rem', margin: '0 0 8px' }}>
              Compte créé !
            </h2>
            <p style={{ fontFamily: 'Lexend', fontSize: 13, color: 'var(--t3)', lineHeight: 1.5, margin: '0 0 20px' }}>
              Un fondateur (Rayan ou Hugo) doit valider ton compte avant que tu puisses te connecter. Reviens dans quelques heures.
            </p>
            <button
              className="asa-btn asa-btn-ghost"
              onClick={() => setMode('login')}
              data-testid="back-to-login-btn"
              style={{ width: '100%' }}
            >
              Retour à la connexion
            </button>
          </div>
        ) : mode === 'login' ? (
          <form onSubmit={submitLogin} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ fontFamily: 'Lexend', fontSize: 11.5, color: 'var(--t3)', paddingLeft: 4, marginBottom: -4 }}>
              Nom / prénom uniquement pour les nouveaux monteurs · sinon tape ton code
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <div>
                <div className="asa-eyebrow" style={{ marginBottom: 6, paddingLeft: 4 }}>Prénom (optionnel)</div>
                <div className="asa-field" style={{ padding: '13px 16px' }}>
                  <input
                    data-testid="login-first-name"
                    placeholder="Jean"
                    name="username"
                    autoComplete="username"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    style={inputStyle}
                  />
                </div>
              </div>
              <div>
                <div className="asa-eyebrow" style={{ marginBottom: 6, paddingLeft: 4 }}>Nom (optionnel)</div>
                <div className="asa-field" style={{ padding: '13px 16px' }}>
                  <input
                    data-testid="login-last-name"
                    placeholder="Dupont"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    style={inputStyle}
                  />
                </div>
              </div>
            </div>
            <div>
              <div className="asa-eyebrow" style={{ marginBottom: 6, paddingLeft: 4 }}>Code d&apos;accès</div>
              <div className="asa-field" style={{ padding: '16px 18px' }}>
                <input
                  data-testid="passcode-input"
                  type="password"
                  name="password"
                  id="asa-passcode"
                  autoComplete="current-password"
                  placeholder="••••••••"
                  autoFocus
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  style={{ ...inputStyle, fontSize: 17, letterSpacing: 2, textAlign: 'center' }}
                />
              </div>
            </div>

            <button
              type="submit"
              data-testid="login-submit"
              className="asa-btn"
              disabled={busy || !code.trim()}
              style={{ height: 50, fontSize: 15, fontWeight: 700, letterSpacing: 0.5, marginTop: 6 }}
            >
              {busy ? <Loader2 size={18} className="spin" /> : 'Se connecter'}
            </button>

            <div style={{
              marginTop: 12, textAlign: 'center', fontFamily: 'Lexend', fontSize: 12.5, color: 'var(--t3)',
            }}>
              Pas encore de compte ?{' '}
              <button
                type="button"
                data-testid="switch-to-signup"
                onClick={() => setMode('signup')}
                style={{
                  background: 'transparent', border: 'none', padding: 0,
                  color: '#5BBEFC', fontFamily: 'Sora', fontWeight: 700, fontSize: 12.5,
                  cursor: 'pointer', textDecoration: 'underline',
                }}
              >Créer un compte</button>
            </div>
          </form>
        ) : (
          <form onSubmit={submitSignup} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <div>
                <div className="asa-eyebrow" style={{ marginBottom: 6, paddingLeft: 4 }}>Prénom</div>
                <div className="asa-field" style={{ padding: '13px 16px' }}>
                  <input data-testid="signup-first-name" placeholder="Jean" value={suFirst} onChange={(e) => setSuFirst(e.target.value)} style={inputStyle} />
                </div>
              </div>
              <div>
                <div className="asa-eyebrow" style={{ marginBottom: 6, paddingLeft: 4 }}>Nom</div>
                <div className="asa-field" style={{ padding: '13px 16px' }}>
                  <input data-testid="signup-last-name" placeholder="Dupont" value={suLast} onChange={(e) => setSuLast(e.target.value)} style={inputStyle} />
                </div>
              </div>
            </div>
            <div>
              <div className="asa-eyebrow" style={{ marginBottom: 6, paddingLeft: 4 }}>Adresse mail</div>
              <div className="asa-field" style={{ padding: '13px 16px' }}>
                <input data-testid="signup-email" type="email" placeholder="jean@studio.io" value={suEmail} onChange={(e) => setSuEmail(e.target.value)} style={inputStyle} />
              </div>
            </div>
            <div>
              <div className="asa-eyebrow" style={{ marginBottom: 6, paddingLeft: 4 }}>Mot de passe</div>
              <div className="asa-field" style={{ padding: '13px 16px' }}>
                <input data-testid="signup-password" type="password" placeholder="6 caractères minimum" value={suPwd} onChange={(e) => setSuPwd(e.target.value)} style={inputStyle} />
              </div>
            </div>

            <button
              type="submit"
              data-testid="signup-submit"
              className="asa-btn"
              disabled={busy}
              style={{ height: 50, fontSize: 15, fontWeight: 700, letterSpacing: 0.5, marginTop: 6 }}
            >
              {busy ? <Loader2 size={18} className="spin" /> : 'Créer mon compte'}
            </button>

            <div style={{
              marginTop: 12, textAlign: 'center', fontFamily: 'Lexend', fontSize: 12.5, color: 'var(--t3)',
            }}>
              Déjà un compte ?{' '}
              <button
                type="button"
                data-testid="switch-to-login"
                onClick={() => setMode('login')}
                style={{
                  background: 'transparent', border: 'none', padding: 0,
                  color: '#5BBEFC', fontFamily: 'Sora', fontWeight: 700, fontSize: 12.5,
                  cursor: 'pointer', textDecoration: 'underline',
                }}
              >Se connecter</button>
            </div>
          </form>
        )}

        <p style={{
          marginTop: 24, textAlign: 'center', color: 'var(--text-dim)',
          fontFamily: 'Lexend', fontSize: 10.5, letterSpacing: 0.5,
        }}>
          ASA · by Elevate Influence
        </p>
      </div>

      <style>{`
        .spin { animation: rotate 0.9s linear infinite; }
        @keyframes rotate { to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}
